/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable camelcase */
import React, {
	useEffect,
	useRef,
	Suspense,
	useState,
	useCallback,
	useMemo,
} from 'react';
import './scss/styles.scss';
import { useSelector, useDispatch } from 'react-redux';
import queryString from 'query-string';
import JwtDecode from 'jwt-decode';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import Socket from './connection/socket';
import { ConnectedUser } from './stores/types/connection';
import Alert from './components/general-components/alert';
import { ROOM_STATES, setSettingsReducerSocket } from './stores/reducers/settings';
import WaitingIndicator from './components/general-components/waiting-indicator';
import Modal from './components/modal/modal';
import { random } from './utils/random';
import * as ErrorHandler from './utils/error-handler';
import UnsupportedSplashPage from './components/errors/unsupported';
import * as Logger from './utils/logger';
import SocketListeners from './socket-listeners/socket-listener-main';
import TempSocketListeners from './socket-listeners/temp-socket-listeners';
import { showAlertLong } from './components/general-components/alert-service';
import { GetActiveRooms } from './connection/join-request';
import AppEntry from './components/app-entry/app-entry';
import MeetingRoom from './components/meeting-room/meeting-room';
import { BROADCAST_STATES, BroadcastStatus } from './stores/types/broadcasting';
import { GreenroomState as GS } from './stores/types/types';
import * as Storage from './utils/local-storage';
import ShowroomsTokensAutoRefresh from './showrooms-tokens-auto-refresh';
import { APP } from './stores/session-status';
import type { ShowroomsTokens } from './stores/types/session';
import {
	getBroadcastServer,
	setPreviousBroadcastState,
	setGoLiveButtonClicked,
} from './stores/actions/broadcasting';
import { replaceState, getFeatureFlags, getFeatureFlagOverrides } from './stores/actions/settings';
import { setGrUser } from './stores/actions/chat';
import { loadVBGs, setLiveChannels } from './stores/actions/content';
import { getPendingJoinRequests } from './stores/actions/invitations';
import {
	setPageStatus,
	setCurrentViewers,
	setAppSignedInWith,
	getProfile,
	verifyAccount,
	setSelectedApp,
	setSession,
	setSelectedMeeting,
} from './stores/actions/session';
import { getStateFromSettingsJson } from './utils/sync';
import type { PresentedMediaItem } from './stores/types/media';
import * as VideoActions from './stores/actions/video';
import { VideoStream } from './components/types/videos';
import { ShotsProvider } from './contexts/shots/provider';
import useLoad from './hooks/use-load';
import UseResourcePool, { ResourcePoolContext } from './hooks/use-resource-pool';
import { AVProvider } from './contexts/audio-video';
import { MediaControlsProvider } from './contexts/media-controls';
import { VideoMachineProvider } from './contexts/video-machine';
import { setBullmanImageName } from './utils/broadcastingImage';
import { OpenTokProvider } from './contexts/opentok/opentok-context';
import LandingPage from './components/landing-page/landing-page';
import { LANDING_PAGE_TYPES } from './types/constants';
import { getCurrentVersion, reasonForNoAccess } from './connection/utils';
import useAmplitude from './hooks/use-amplitude';
import { getCommToggle } from './connection/comm-toggle';
import { handleClickJacking } from './utils/utils';
import { UploadProvider } from './contexts/upload-context';
import * as ModalActions from './stores/actions/modal';
import { MODAL_TEMPLATES } from './stores/reducers/modal';
import { EntityType } from './types/enums';
import { setLogMeetingId, setLogUser } from './utils/datadog';
import { loadGenerateContentTemplates, loadGeneratedContent } from './stores/actions/generate-content';
import { SocketProvider } from './contexts/socket';
import VideoCacheProvider from './contexts/media/video-queue';
import StandaloneDeprecationWarning from './components/app-entry/standalone-deprecation-modal';
import store from './stores/main';
import { setFeatureFlags as setBlpFeatureFlags } from './stores/actions/blp-feature-flags';
import GetBLPFeatureFlags from './connection/blp-feature-flags';
import { selectFeatureFlagsLoading } from './stores/selectors/blp-feature-flags';
import useDowntimeToast from './hooks/use-downtime-toast';
import { MediaDevicesProvider } from './contexts/media/check-media';
import useBroadcastState from './components/broadcasting/hooks/useBroadcastState';
import useDetectMultiAccount from './hooks/users/use-detect-multi-account';
import OnsiteLocationMain from './components/app-entry/onsite-location/onsite-location-main';
import { ProgramViewType } from './stores/types/session';
import SolutionCenter from './components/solution-center/solution-center';
import { OnsiteLocationSessionRoute } from './stores/types/video';
import { ThemeProvider } from './contexts/theme-context';
import '@brandlive/pixel/pixel.css';

// testing auto build 3
const MetaMenu = React.lazy(() => import('./components/meta-menu/meta-menu'));

const isProduction = window.location.origin.includes('brandlive.com');

export interface StartOptions {
	room?: string;
	agora?: string;
	twilio?: string;
	host?: string;
	showAppEntry?: string;
	embed?: string;
	showMedia?: string;
	broadcasting?: string;
	iosEmulate?: string;
	skipBrowser?: string; // skip this
}

const startOptions: StartOptions = queryString.parse(window.location.search);

const session = random(10);

const ua = window.navigator.userAgent;
const isIE = (!!window as any).MSInputMethodContext && !!(document as any).documentMode;
const isSafari = !!ua.match(/Safari/i) && !ua.match(/Chrome/i);
const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
const isAndroid = !!ua.match(/android/i);
const iOSAgents = ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'];
const isiOS = (
	iOSAgents.includes(navigator.userAgent)
	// iPad on iOS 13 detection
	|| (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
);

function getSocketChannel(live_chat: number | string): string {
	if (window.location.hostname.includes('brandlive.com')) {
		return `bl${live_chat}`;
	}
	return `staging-bl${live_chat}`;
}

function getSocketEndpoint() {
	return window.location.hostname.includes('brandlive.com') ? 'ion.brand.live' : 'ion2.brand.live';
}

function debugLog(...args: any[]) {
	const isDebug = window.location.search.includes('debug');
	if (isDebug && !isProduction) {
		console.log(...args);
	}
}

function getEventRoom(liveChat: string | number) {
	if (isProduction) {
		return `bl${liveChat}`;
	}
	return `staging-bl${liveChat}`;
}

function checkEquality(obj: any, obj2: any) {
	if (!obj) return false;
	const keys = Object.keys(obj);
	for (let i = 0; i < keys.length; i += 1) {
		const key = keys[i];
		if (typeof obj2[key] === 'undefined') {
			debugLog('missing key', key);
			return false;
		}
		if (obj[key] !== obj2[key]) {
			debugLog('difference', key, obj[key], obj2[key]);
			return false;
		}
	}

	return true;
}

// redirect to the correct production url
if (window.location.hostname.includes('greenroom.brand.live')) {
	const dontRedirect = window.location.pathname.includes('create-account')
	|| window.location.pathname.includes('password-reset');

	if (!dontRedirect) {
		const search = window.location.search ? `?${window.location.search}` : '';
		window.location.href = `https://greenroom.brandlive.com${window.location.pathname}${search}`;
	}
}

export default function App() {
	useAmplitude();
	useDowntimeToast();
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const pageStateError = useSelector((s: GS) => s.SessionReducer.pageStateError);
	const pageStateLoading = useSelector((s: GS) => s.SessionReducer.pageStateLoading);
	const onboardingComplete = useSelector((s: GS) => s.SessionReducer.onboardingComplete);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const webrtcID = useSelector((s: GS) => s.SessionReducer.webrtcID);
	const screenShareWebrtcId = useSelector((s: GS) => s.SessionReducer.screenShareWebrtcId);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const showroomsTokens = useSelector((s: GS) => s.SessionReducer.showroomsTokens);
	const appSignedInWith = useSelector((s: GS) => s.SessionReducer.appSignedInWith);
	const restrictedUserState = useSelector((s: GS) => s.SessionReducer.restrictedUserState);
	const watcher_only = useSelector((s: GS) => s.SessionReducer.watcher_only);
	const programView = useSelector((s: GS) => s.SessionReducer.programView);
	const limitedInput = useSelector((s: GS) => s.SessionReducer.limitedInput);
	const events = useSelector((s: GS) => s.ContentReducer.events);
	const videoMuted = useSelector((s: GS) => s.VideoReducer.videoMuted);
	const audioMuted = useSelector((s: GS) => s.VideoReducer.audioMuted);
	const isLive = useSelector((s: GS) => s.VideoReducer.isLive);
	const connectedPresenterVideos = useSelector((s: GS) => s.VideoReducer.connectedPresenterVideos);
	const connectedMediaVideos = useSelector((s: GS) => s.VideoReducer.connectedMediaVideos);
	const feedWebRTCIds = useSelector((s: GS) => s.VideoReducer.feedWebRTCIds);
	const serverState = useSelector((s: GS) => s.BroadcastReducer.serverState);
	const previousServerState = useSelector((s: GS) => s.BroadcastReducer.previousServerState);
	const goLiveButtonClicked = useSelector((s: GS) => s.BroadcastReducer.goLiveButtonClicked);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const currentShotId = useSelector((s: GS) => s.ShotsReducer.currentShotId);
	const hasFetchedFeatureFlags = useSelector((s: GS) => s.SettingsReducer.hasFetchedFeatureFlags);
	const hasSynced = useSelector((s: GS) => s.SettingsReducer.hasSynced);
	const featureFlags = useSelector((s: GS) => s.SettingsReducer.featureFlags);
	const presentedMedia = useSelector((s: GS) => s.MediaReducer.presentedMedia);
	const accountDeleted = useSelector((s: GS) => s.SessionReducer.accountDeleted);
	const showDeprecation = useSelector((s: GS) => s.UserInterfaceReducer.showDeprecationWarning);
	const homeMeeting = useSelector((s: GS) => s.HomeReducer?.homeMeeting);
	const blpFlagsLoading = useSelector(selectFeatureFlagsLoading);
	const companionMode = useSelector((s: GS) => s.SessionReducer.companionMode);
	const onsiteLocationSession = useSelector((s: GS) => s.VideoReducer.onsiteLocationSession);
	const shotModalShot = useSelector((s: GS) => s.ShotsReducer.shotModalShot);
	const {
		use_solution_center_panel: useSolutionCenterPanel,
	} = useSelector((s: GS) => s.SettingsReducer.featureFlags);

	const dispatch = useDispatch();
	const [connected, setConnected] = useState(false);
	const [tempConnected, setTempConnected] = useState(false);
	const [hasReplacedSettingsJson, setHasReplacedSettingsJson] = useState(false);
	const videoMutedRef = useRef(videoMuted);
	const audioMutedRef = useRef(audioMuted);
	const userRef = useRef<ConnectedUser | null>(null);
	const checkLiveChannelsInterval = useRef<NodeJS.Timeout | null>(null);
	const hasSentWebrtcId = useRef(false);
	const hasSentOnboardingComplete = useRef(false);
	const socket: React.MutableRefObject<Socket | null> = useRef(null);
	const eventSocket: React.MutableRefObject<Socket | null> = useRef(null);
	const tempSocket = useRef<Socket | null>(null);
	const viewerInterval = useRef<NodeJS.Timeout | null>(null);

	const resourcePool = UseResourcePool();

	const { location } = window;
	const queryParams = new URLSearchParams(location.search);
	const accessDenied = queryParams.get('accessDenied');

	const { broadcastStatus } = useBroadcastState();
	const { loadMeetingRoom } = useLoad();
	useDetectMultiAccount();

	function requestLiveChannels() {
		GetActiveRooms(token as string).then((res) => {
			const channelSet = new Set(res);
			dispatch(setLiveChannels(Array.from(channelSet)));
		});
	}

	useEffect(() => {
		if (accessDenied) {
			const reason: string | null = queryParams.get('reason');
			// Remove the query parameter from url
			window.history.replaceState({}, 'Greenroom', host ? '/producer' : '/');
			dispatch(ModalActions.setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.NO_ROOM_ACCESS,
				modalData: {host, reason}
			}));
		}
	}, [accessDenied, host, location]);

	const fetchFeatureFlags = useCallback(async () => {
		const flags = await GetBLPFeatureFlags();
		dispatch(setBlpFeatureFlags(flags));
	}, [dispatch]);

	useEffect(() => {
		fetchFeatureFlags();
	}, [fetchFeatureFlags]);

	useEffect(() => {
		setBullmanImageName();
		handleClickJacking();
	}, []);

	useEffect(() => {
		// use homeMeeting for the show details view
		if ((meeting || homeMeeting)) {
			dispatch(getFeatureFlagOverrides(meeting?.meeting ?? homeMeeting.meeting));
		}
	}, [meeting?.meeting, homeMeeting?.meeting]);

	useEffect(() => {
		if (meeting && token) {
			dispatch(loadGenerateContentTemplates(token));
			dispatch(loadGeneratedContent(token, meeting));
		}
	}, [meeting, token]);

	useEffect(() => {
		if (token) {
			getCurrentVersion(token, dispatch)
				.catch((error) => {
					console.error(error);
				});
		}
	}, [token]);

	const [hasRequestedCommState, setHasRequestedCommState] = useState(false);
	useEffect(() => {
		if (token && meeting) {
			if (hasRequestedCommState) return;

			setHasRequestedCommState(true);

			getCommToggle(meeting.meeting, token)
				.then((commToggleState) => {
					dispatch({ type: EntityType.commsToggle, payload: commToggleState });
				});
		}
	}, [meeting, token, hasRequestedCommState]);

	const isOnsiteLocation = useMemo(
		() => programView === ProgramViewType.OnsiteLocation
			&& !( // limited input canvas without an id is the logged in presenter
				onsiteLocationSession?.route === OnsiteLocationSessionRoute.LimitedInputCanvas
				&& !onsiteLocationSession?.id
			),
		[programView, onsiteLocationSession?.route, onsiteLocationSession?.id],
	);

	// attempted to migrate - this will be more difficult
	useEffect(() => {
		// the user hasn't been set yet, we can safely ignore this whole section
		if (!user) {
			return;
		}

		// build user object
		const userObject = {
			first_name: user.first_name,
			last_name: user.last_name,
			email: user.email,
			title: user.title,
			avatar: user.avatar,
			gr_user: user.gr_user,
			webrtcID,
			screenShareWebrtcId,
			feedWebRTCIds,
			isProducer: host,
			location: user.location,
			session,
			poster: user.bg_image,
			in_room: onboardingComplete && !isOnsiteLocation,
			restrictedUserState,
			watcher_only,
			selectedShowbuilderShot: roomState === ROOM_STATES.EDITING ? currentShotId : null,
			companionMode,
			onsiteLocationSession, // can remove once we allow multiple publishers per location
			shotModalId: shotModalShot.shot_id,
		};

		// check if anything has actually changed since the last time this was updated
		// if there are changes, update the user accordingly.
		if (
			!checkEquality(userRef.current, userObject)
			|| (webrtcID && !hasSentWebrtcId.current)
			|| (
				onboardingComplete
				&& !hasSentOnboardingComplete.current
			)
		) {
			// something changed, update the ref and send the updated user object
			// @ts-ignore
			userRef.current = { ...userObject };
			// @ts-ignore
			socket.current?.updateUser(userRef.current);

			if (webrtcID) {
				hasSentWebrtcId.current = true;
			}

			if (onboardingComplete) {
				hasSentOnboardingComplete.current = true;
			}
		}

		// muted has changed, send update
		if (videoMutedRef.current !== videoMuted && webrtcID) {
			videoMutedRef.current = videoMuted;
		}

		// muted has changed, send update
		if (audioMutedRef.current !== audioMuted && webrtcID) {
			audioMutedRef.current = audioMuted;
		}
	}, [
		webrtcID,
		screenShareWebrtcId,
		videoMuted,
		audioMuted,
		user,
		host,
		isLive,
		onboardingComplete,
		restrictedUserState,
		roomState,
		currentShotId,
		watcher_only,
		connectedPresenterVideos,
		companionMode,
		onsiteLocationSession,
		shotModalShot,
	]);

	useEffect(() => {
		if (meeting && !hasReplacedSettingsJson && hasFetchedFeatureFlags) {
			// if we are using new sync, make sure we have current synced state before applying
			// settings_json this is so that we can remove stale state from the settings json,
			// and replace it with the synced state
			if (hasSynced) {
				dispatch(replaceState(getStateFromSettingsJson(meeting.settings_json)));
				setHasReplacedSettingsJson(true);
			}
		}
	}, [
		meeting,
		hasSynced,
		hasReplacedSettingsJson,
		hasFetchedFeatureFlags,
		dispatch,
	]);

	useEffect(() => {
		dispatch(getFeatureFlags());
	}, []);

	useEffect(() => {
		if (blpFlagsLoading) return;

		if (!meeting) dispatch(setPageStatus());

		if (!socket.current && meeting && (user || embed)) {
			const storedVBG = localStorage.getItem('stored_vbg');
			if (storedVBG && token && user) {
				dispatch(loadVBGs(token, user.gr_user));
			}

			if (!embed && meeting.brandlive_event) {
				eventSocket.current = new Socket(
					getSocketChannel(meeting.brandlive_event),
					getSocketEndpoint(),
					() => {
						if (meeting.brandlive_event) {
							eventSocket.current?.getCurrentlyWatching(
								getEventRoom(meeting.brandlive_event),
							).then((num: number) => {
								dispatch(setCurrentViewers(num));
							});

							viewerInterval.current = setInterval(() => {
								if (meeting.brandlive_event) {
									eventSocket.current?.getCurrentlyWatching(
										getEventRoom(meeting.brandlive_event),
									).then((num: number) => {
										dispatch(setCurrentViewers(num));
									});
								}
							}, 10000);
						}
					},
				);
			}

			socket.current = new Socket(
				`meeting-${meeting.meeting}`,
				undefined,
				() => {
					if (!socket.current) {
						showAlertLong({
							message: 'Unable to connect',
							description: 'We were unable to get a connection to the Producer, please '
								+ 'refresh your page and try again',
							type: 'error',
						});
						return;
					}

					SocketListeners(socket.current);

					if ((!embed || (embed && limitedInput)) && user) {
						dispatch(setGrUser(user.gr_user));

						socket.current.userConnected({
							first_name: user.first_name,
							last_name: user.last_name,
							email: user.email,
							title: user.title,
							avatar: user.avatar ?? '',
							gr_user: user.gr_user,
							isProducer: host,
							location: user.location,
							session,
							poster: user.bg_image,
							in_room: userRef.current?.in_room
								? userRef.current.in_room
								: onboardingComplete && !isOnsiteLocation,
							restrictedUserState: userRef.current?.restrictedUserState
								? userRef.current.restrictedUserState
								: restrictedUserState,
							watcher_only,
							selectedShowbuilderShot: roomState === ROOM_STATES.EDITING
								? currentShotId
								: null,
							last_modified: user.last_modified,
							companionMode,
							onsiteLocationSession, // can remove once we allow multiple publishers per location
							shotModalId: shotModalShot.shot_id,
						});
					} else {
						socket.current.getUsers();
					}

					if (host && token) {
						setSettingsReducerSocket(socket.current);
					}

					if ((!embed || (embed && limitedInput)) && token && user) {
						// don't start a Bullman server if the Spielberg flag is on when first joining a meeting
						const useSpielberg = store.getState().SettingsReducer.featureFlags.use_spielberg;
						if (!useSpielberg) {
							dispatch(getBroadcastServer(token, meeting.meeting));
						}
						socket.current.addNewChannel(String(user.gr_user));

						// close the temp socket connection when we enter the event
						tempSocket.current?.disconnect();
						tempSocket.current = null;

						if (checkLiveChannelsInterval.current) {
							clearInterval(checkLiveChannelsInterval.current);
						}

						requestLiveChannels();

						// checking the live channels can miss a producer if they connect at the right
						// moment - here we are going to just check periodically to see if the channel
						// is live
						if (checkLiveChannelsInterval.current) {
							clearInterval(checkLiveChannelsInterval.current);
						}
						if (!onboardingComplete) {
							checkLiveChannelsInterval.current = setInterval(requestLiveChannels, 5000);
						}

						Logger.log({ load_meeting: meeting });

						socket.current.sendMessage('joined-meeting', meeting, String(user.gr_user));
					}

					setConnected(true);

					if (host && token) {
						setTimeout(() => dispatch(getPendingJoinRequests(token, meeting.meeting)), 500);
					}
				},
			);
		}

		setLogUser(user);
		setLogMeetingId(meeting?.meeting_id?.toString());

		// when onboarding is complete we no longer need to check if the channel is live or not
		if (onboardingComplete && checkLiveChannelsInterval.current) {
			clearInterval(checkLiveChannelsInterval.current);
		}
	}, [onboardingComplete, meeting, embed, blpFlagsLoading]);

	useEffect(() => {
		if (
			meeting
			&& token
			&& socket.current
			&& !socket.current.onReconnected
		) {
			socket.current.onReconnected = () => {
				loadMeetingRoom({
					passedInToken: token,
					passedInMeeting: meeting,
					embed,
					isReload: true,
					showroomsTokens,
				});
			};
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		embed,
		featureFlags,
		loadMeetingRoom,
		meeting,
		showroomsTokens,
		socket.current,
		token,
	]);

	useEffect(() => {
		// the temp socket just exists to check how many live channels there are right now
		if (user && events?.length > 0 && tempSocket.current) {
			requestLiveChannels();

			// checking the live channels can miss a producer if they connect at the right moment -
			// here we are going to just check periodically to see if the channel is live
			if (checkLiveChannelsInterval.current) {
				clearInterval(checkLiveChannelsInterval.current);
			}

			checkLiveChannelsInterval.current = setInterval(requestLiveChannels, 5000);
		}
	}, [events]);

	useEffect(() => {
		// the temp socket just exists to check how many live channels there are right now
		if (user?.gr_user) {
			if (tempSocket.current) {
				tempSocket.current.disconnect();
			}

			tempSocket.current = new Socket(
				`temp-${user.email.toLowerCase().trim()}`,
				undefined,
				() => {
					if (!tempSocket.current) {
						showAlertLong({
							message: 'Unable to connect',
							description: 'We were unable to get a connection to the Producer, please refresh your page and try again',
							type: 'error',
						});
						return;
					}

					TempSocketListeners(tempSocket.current);
					setTempConnected(true);
				},
			);
		}
	}, [user?.gr_user]);

	useEffect(() => {
		// disable all react-beautiful-dnd development warnings - our dnd loves to throw TONS of
		// warnings out about how we can't have "nested scroll containers" I haven't found this to
		// cause any issues so far, but if debugging, probably want to disable this
		// @ts-ignore
		window['__react-beautiful-dnd-disable-dev-warnings'] = !process.env.ENABLE_DND_WARNINGS;
	}, []);

	useEffect(() => {
		// If the user opened the app from an email link to "create account" or "reset password" and
		// someone was already logged in, don't auto load that user.
		if (
			!window.location.href.includes('create-account')
			&& !window.location.href.includes('password-reset')
			&& !blpFlagsLoading
			&& !pageStateLoading
		) {
			// Load in some storage items that are used around the app
			const appSignedInWithStorage = Storage.getStorageItem('app-signed-in-with');
			if (appSignedInWithStorage) {
				dispatch(setAppSignedInWith(appSignedInWithStorage));
			}
			const storedShowroomsTokensJWT = Storage.getStorageItem('showrooms-tokens');
			let showroomsTokenData;
			if (storedShowroomsTokensJWT) {
				showroomsTokenData = JwtDecode(storedShowroomsTokensJWT) as ShowroomsTokens;
			}
			if (token && token !== process.env.REACT_APP_SYNC_AUTH) {
				// Reload profile every time the app opens so its always up to date
				dispatch(getProfile(token, appSignedInWithStorage || 'events', showroomsTokenData?.accessToken));
			}
		}
	}, [blpFlagsLoading, pageStateLoading]);

	useEffect(() => {
		// If a verification code exists in the URL but not "create-account" in the path, attempt to
		// verify the user. This means a presenter opened GR, created an account manually, got the
		// verification email, and clicked the link. So because they already created an account, all
		// we have to do is verify them. (vs. not having an account, being invited to a GR, taken to
		// the create account from the email link, auto verifying, and advancing to the GR meeting)
		const url = new URL(window.location.href);
		const code = url.searchParams.get('verify');
		const createAccount = window.location.href.indexOf('create-account') > -1;

		if (code && !createAccount) {
			dispatch(verifyAccount(code));
		}

		// Check if there's an "app" param in the URL which is used for password reset for a
		// producer. When a producer opens a password reset link from their email, we use this param
		// to set the "selectedApp" state variable so we know which app to sign the producer into
		// when they navigate back to the login form
		const app = url.searchParams.get('app');
		if (app) {
			dispatch(setSelectedApp(app));
		}
	}, []);

	useEffect(() => {
		if (pageStateError) {
			console.error(pageStateError);
		}
	}, [pageStateError]);

	useEffect(() => {
		if (token && process.env.NODE_ENV === 'production') {
			ErrorHandler.setErrorToken(token);
		}

		Logger.setUser(user);
		Logger.setSession(session);
		dispatch(setSession(session));
	}, [token]);

	useEffect(() => {
		if (process.env.NODE_ENV === 'production') ErrorHandler.setupErrorHandling();
	}, []);

	useEffect(() => {
		// When a broadcast is live we want to know if it was already live when the page was loaded
		// or if the producer just started it. If the broadcast just went live the LIVE timer should
		// start at 00:00. If broadcast was already live, we want the LIVE timer to have elapsed
		// from when the broadcast originally went live. So we save the previous server state
		// locally and if we see the states change from BROADCAST_STARTING to BROADCAST_STARTED,
		// we know to reset broadcast_live_start_time to now (broadcast_live_start_time comes from
		// start_time in gr_broadcasts. This is what's passed into <LiveCounter>. When this value
		// changes on the backend, we don't get the update until reloading the page)
		if (
			previousServerState === BROADCAST_STATES.BROADCAST_STARTING
			&& serverState === BROADCAST_STATES.BROADCAST_STARTED
		) {
			dispatch(setSelectedMeeting({
				...meeting,
				broadcast_live_start_time: new Date(),
			}));
		}

		// Update previous state after check
		dispatch(setPreviousBroadcastState(serverState));
	}, [serverState]);

	useEffect(() => {
		// SYNC SOLUTION (not sockets): This fires anytime presentedMedia is changed. That includes
		// anyone who:
		//	- Presents/stops presenting
		//	- Changes a page

		// Convert presentedMedia -> connectedMediaVideos to display in video-feeds
		const videoStreamsForMedia = presentedMedia.map((med: PresentedMediaItem) => ({
			id: `media-share-${med.mediaId}`,
			videoMuted: false,
			muted: false,
			isMedia: true,
			mediaId: med.mediaId,
		} as VideoStream));

		// If the only thing that changed was the page, we don't need to update connectedMediaVideos
		// because the media is already in video feeds
		const mediaIdsInNewFeeds = videoStreamsForMedia.map(
			(vid: VideoStream) => vid.mediaId,
		).filter((id) => !!id);
		const mediaIdsInOldFeeds = connectedMediaVideos.map(
			(vid: VideoStream) => vid.mediaId,
		).filter((id) => !!id);
		if (mediaIdsInNewFeeds.sort().join(',') !== mediaIdsInOldFeeds.sort().join(',')) {
			dispatch(VideoActions.setConnectedMediaVideos([...videoStreamsForMedia as VideoStream[]]));
		}
	}, [presentedMedia]);

	useEffect(() => {
		const isActive = broadcastStatus === BroadcastStatus.active
			|| broadcastStatus === BroadcastStatus.starting;

		// If you have refreshed and broadcasting has started or is
		// starting, goLiveButtonClicked will be false. Change to true so the user
		// cannot modify the destination.
		if (isActive && !goLiveButtonClicked) {
			dispatch(setGoLiveButtonClicked(true));
		}

		// If the user has ended the broadcast or it has failed, change
		// goLiveButtonClicked to false so the user
		// can modify the destination.
		if (!isActive && goLiveButtonClicked) {
			dispatch(setGoLiveButtonClicked(false));
		}
	}, [broadcastStatus, goLiveButtonClicked]);

	useEffect(() => {
		if (onboardingComplete && window.sessionStorage.getItem('skipOnboardingOnReload') !== 'true') {
			window.sessionStorage.setItem('skipOnboardingOnReload', 'true');
		}
	}, [onboardingComplete]);

	useEffect(() => {
		const navigationEntries = window.performance.getEntriesByType('navigation');
		// @ts-ignore
		const reload = navigationEntries.length > 0 && navigationEntries[0].type === 'reload';
		const goHomeOnReload = sessionStorage.getItem('goHomeOnReload') === 'true';
		if (reload && goHomeOnReload) {
			window.location.href = '/';
		}
	}, []);

	if (isiOS) {
		return <UnsupportedSplashPage ios />;
	}	if (isAndroid) {
		return <UnsupportedSplashPage android />;
	} if (!startOptions.skipBrowser && (isIE || isSafari || isFirefox)) {
		return <UnsupportedSplashPage />;
	}

	return (
		<ThemeProvider>
			<>
				{(showDeprecation && host) ? <StandaloneDeprecationWarning /> : null}
				<div className='generic-tooltip' id='generic-tooltip' />
				<DndProvider backend={HTML5Backend}>
				{appSignedInWith === APP.SHOWROOMS
					&& <ShowroomsTokensAutoRefresh />
				}

				{accountDeleted && <LandingPage type={LANDING_PAGE_TYPES.ACCOUNT_DELETED} />}

				{!accountDeleted && <> {!pageStateLoading ? (
						<ResourcePoolContext.Provider value={resourcePool}>
							<AVProvider>
								<SocketProvider socket={socket?.current ?? tempSocket?.current ?? null}>
									<MediaControlsProvider>
										<OpenTokProvider socket={socket.current}>
											<VideoMachineProvider>
												<ShotsProvider>
													<VideoCacheProvider>
														<MediaDevicesProvider>
															<UploadProvider>
																{(onboardingComplete || embed) && connected && meeting
																	? (
																		<>
																			{onsiteLocationSession ? (
																				<OnsiteLocationMain
																					onsiteLocationSession={onsiteLocationSession}
																				/>
																			) : (
																				<MeetingRoom
																					startOptions={startOptions}
																					socket={socket.current}
																				/>
																			)}
																		</>
																	) : (
																		<AppEntry
																			socket={socket.current ? socket.current : tempSocket.current}
																			connected={connected || tempConnected}
																		/>
																	)
																}
																{useSolutionCenterPanel && user && (
																	<Suspense fallback={<></>}>
																		{<SolutionCenter />}
																	</Suspense>
																)}
																{user && (
																	<Suspense fallback={<></>}>
																		{ <MetaMenu /> }
																	</Suspense>
																)}
																{user && (
																	<Suspense fallback={<></>}>
																		{<Modal socket={socket.current} />}
																	</Suspense>
																)}
															</UploadProvider>
														</MediaDevicesProvider>
													</VideoCacheProvider>
												</ShotsProvider>
											</VideoMachineProvider>
										</OpenTokProvider>
									</MediaControlsProvider>
								</SocketProvider>
							</AVProvider>
						</ResourcePoolContext.Provider>
				) : (
					<div className="wrapper loading-app">
						<WaitingIndicator />
					</div>
				)}
					{!embed && <Alert socket={socket.current} />}
				</>
				}
			</DndProvider>
		</>
		</ThemeProvider>
	);
}
